import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { chunkArray } from "@event-planner/shared";
import type {
  EventParticipant,
  ParticipantStatus,
  Person,
} from "@event-planner/shared";

interface UseEventParticipantsResult {
  participants: EventParticipant[];
  /** Persons for the matched participants, keyed off `personId`. */
  persons: Person[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Two-stage fetch: query `eventParticipants` by eventId + status (default
 * active + pending), then chunked `persons` lookups for the resolved
 * personIds.
 *
 * ADR-136/137 invariant: pending participants are real people. Most admin
 * surfaces are resolving person labels or audience targets, so they should
 * include pending rows by default. Callers that truly need claimed-only
 * people must opt into `statuses: ['active']` explicitly.
 *
 * Replaces the legacy `useEventMembers` (eventMemberships + users) under
 * ADR-136.
 */
export function useEventParticipants(
  eventId: string | undefined,
  statuses: ParticipantStatus[] = ["active", "pending"],
): UseEventParticipantsResult {
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  // After the first load, refetches update rows in place instead of
  // flashing the table's loading state (same rule as useFirestoreCollectionOnce).
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [version, setVersion] = useState(0);

  // Stable key for the statuses array so the effect doesn't churn on every
  // render when the caller passes an inline literal.
  const statusKey = useMemo(() => statuses.join(","), [statuses]);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!eventId) {
      setParticipants([]);
      setPersons([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (!initialLoadDone) setLoading(true);

    async function fetchAll() {
      try {
        const statusList = statusKey.split(",") as ParticipantStatus[];
        // Firestore `where('status', 'in', [...])` supports up to 30
        // values, plenty for our 3-value status enum.
        const participantsSnap = await getDocs(
          query(
            collection(db, "eventParticipants"),
            where("eventId", "==", eventId),
            where("status", "in", statusList),
          ),
        );
        if (cancelled) return;

        const fetchedParticipants = participantsSnap.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as EventParticipant,
        );
        setParticipants(fetchedParticipants);

        const personIds = Array.from(
          new Set(fetchedParticipants.map((p) => p.personId)),
        );
        if (personIds.length === 0) {
          if (!cancelled) {
            setPersons([]);
            setError(null);
          }
          return;
        }

        const chunks = chunkArray(personIds, 30);
        const chunkSnapshots = await Promise.all(
          chunks.map((chunk) =>
            getDocs(
              query(
                collection(db, "persons"),
                where(documentId(), "in", chunk),
              ),
            ),
          ),
        );
        if (cancelled) return;

        const personMap = new Map<string, Person>();
        for (const snapshot of chunkSnapshots) {
          for (const d of snapshot.docs) {
            personMap.set(d.id, { id: d.id, ...d.data() } as Person);
          }
        }
        setPersons(Array.from(personMap.values()));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoadDone(true);
        }
      }
    }

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [eventId, version, statusKey]);

  return { participants, persons, loading, error, refetch };
}
